import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { tap, map, exhaustMap, catchError } from 'rxjs/operators';
import { LoginService } from '../services';
import * as AuthActions from './auth.actions';
import { Authenticate } from '@kanzi-apes/kanzi-models';
import { AlertService } from '@kanzi-apes/kanzi-alerts';

@Injectable()
export class AuthEffects {
  /**
   * Effect to login user.
   */
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      map((action) => action.loginForm),
      exhaustMap((auth: Authenticate) =>
        this.authService.login(auth).pipe(
          map((token) =>
            token
              ? AuthActions.loginSuccess({ token })
              : AuthActions.userLoginFailureToken()
          ),
          catchError((error) =>
            of(
              AuthActions.loginFailure({
                error: {
                  name: error.name,
                  message: error.error.non_field_errors
                    ? error.error.non_field_errors[0]
                    : error.message,
                },
              })
            )
          )
        )
      )
    )
  );

  /**
   * Effect to navigate to home page when login is successful.
   */
  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(() => this.router.navigate(['/']))
      ),
    { dispatch: false }
  );

  /**
   * Effect to navigate to login page when login is redirected or logout is triggered.
   */
  loginRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginRedirect, AuthActions.logout),
        tap((authed) => {
          this.router.navigate(['/auth/login']);
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect to load user profile.
   */
  userProfileLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.userProfileLoad),
      map((action) => action),
      exhaustMap(() =>
        this.authService
          .userProfile()
          .pipe(map((user) => AuthActions.userProfileSuccess({ user })))
      )
    )
  );

  loginFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loginFailure),
        tap((action) => this.alertService.error(action.error.message))
      );
    },
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private authService: LoginService,
    private router: Router,
    private alertService: AlertService
  ) {}
}
